import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarCheck,
  UserCog,
  Settings,
  LogOut,
  Menu,
  X,
  CreditCard,
  ShieldCheck,
  Sparkles,
  BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLeads } from "@/context/LeadsContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import BrandLogo from "@/components/BrandLogo";
import DemoBanner from "@/components/DemoBanner";

const SAAS_OWNER_EMAIL = "corasaniti.alessandro79@gmail.com";

const BASE_NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/clients", label: "Clienti", icon: Users, testid: "nav-clients" },
  { to: "/app/agenda", label: "Agenda", icon: Calendar, testid: "nav-agenda" },
  { to: "/app/events", label: "Eventi", icon: CalendarCheck, testid: "nav-events" },
  { to: "/app/team", label: "Team", icon: UserCog, testid: "nav-team" },
  { to: "/app/ai-manager", label: "AI Manager", icon: Sparkles, testid: "nav-ai-manager" },
  { to: "/app/plans", label: "Piano", icon: CreditCard, testid: "nav-plans" },
  { to: "/app/settings", label: "Impostazioni", icon: Settings, testid: "nav-settings" },
  // Guida lives outside the /app namespace (see Route in App.js) but we
  // surface it here so users can reach it without scrolling to the footer.
  // The page itself and routing are unchanged — sidebar entry only.
  { to: "/guida", label: "Guida", icon: BookOpen, testid: "nav-guide" },
];
const ADMIN_NAV = { to: "/app/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" };

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { count: unreadLeads } = useLeads();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isOwner = (user?.email || "").toLowerCase() === SAAS_OWNER_EMAIL.toLowerCase();
  const NAV = isOwner ? [...BASE_NAV, ADMIN_NAV] : BASE_NAV;

  const handleLogout = async () => {
    await logout();
    toast.success("Disconnessione effettuata");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-border transition-transform flex flex-col`}
        data-testid="app-sidebar"
      >
        <div className="h-16 px-6 flex items-center border-b border-border">
          <Link to="/app/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
            <BrandLogo size={32} />
            <span className="font-serif-heading text-lg">AI Bridal</span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-all border-l-2 ${
                  isActive
                    ? "border-primary text-primary bg-primary/5 font-medium"
                    : "border-transparent text-neutral-700 hover:text-primary hover:bg-secondary"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.5} />
              <span className="flex-1">{n.label}</span>
              {n.to === "/app/clients" && unreadLeads > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold tabular-nums"
                  data-testid="sidebar-leads-badge"
                  aria-label={`${unreadLeads} nuove richieste preventivo`}
                >
                  {unreadLeads > 99 ? "99+" : unreadLeads}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="px-3 py-2 text-xs">
            <div className="label-uppercase">Account</div>
            <div className="mt-1 text-sm font-medium truncate" data-testid="sidebar-username">{user?.name || user?.email}</div>
            <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 hover:text-destructive rounded-sm transition-colors"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Esci
          </button>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-white/80 backdrop-blur-md lg:hidden flex items-center px-4 sticky top-0 z-20">
          <button onClick={() => setOpen(!open)} className="p-2" data-testid="sidebar-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <span className="ml-3 font-serif-heading text-lg flex items-center gap-2">
            <BrandLogo size={26} /> AI Bridal Manager™
          </span>
        </header>
        <main className="flex-1 p-6 lg:p-10 overflow-auto">
          {/* Demo banner — renders only when the user owns a demo client.
              Lives ABOVE the main padding so it spans the full content
              width but inside the scroll container so it scrolls with
              the page (no sticky behaviour: keep it discoverable, not
              intrusive). */}
          <div className="-mx-6 lg:-mx-10 -mt-6 lg:-mt-10 mb-6 lg:mb-10">
            <DemoBanner />
          </div>
          <Outlet />
        </main>
        <Footer variant="minimal" />
      </div>
    </div>
  );
}
